import invariant from 'tiny-invariant';
import type { SeriesForPresentation } from '@mentimeter/http-clients';
import { v4 as uuidv4 } from 'uuid';
import { addBreadcrumb } from '@mentimeter/errors/sentry';
import type { InternalState, QRCodePlacement, SlideState } from './types';

const getSlide = (slidePublicKey: string, series: SeriesForPresentation) =>
  series.questions.find((s) => s.public_key === slidePublicKey);

const INITIAL_QUIZ_SLIDE_STATE: SlideState = {
  started: false,
  starting: false,
};

const START_QUIZ_SLIDE_STATE: SlideState = {
  started: false,
  starting: true,
};

interface GetNextStepProps {
  hasNewNextStep: boolean;
  linearNavigationSteps: string[];
  newStep: number;
  hasNextSlide: boolean;
  isInQuizLobby: boolean;
  nextSlideInitialStep: string | undefined;
}

const getNextStep = ({
  hasNewNextStep,
  linearNavigationSteps,
  newStep,
  hasNextSlide,
  isInQuizLobby,
  nextSlideInitialStep,
}: GetNextStepProps) => {
  if (isInQuizLobby) {
    return 'start-quiz';
  }

  if (hasNewNextStep) {
    return linearNavigationSteps[newStep + 1];
  }

  if (nextSlideInitialStep === 'end-presentation') {
    return 'end-presentation';
  }

  if (nextSlideInitialStep === 'leaderboard') {
    return 'leaderboard';
  }

  if (hasNextSlide) {
    return 'next-slide';
  }

  return undefined;
};

const getNextSlideInitialStep = ({
  slideIndex,
  series,
}: {
  slideIndex: number;
  series: SeriesForPresentation;
}) => {
  if (slideIndex + 1 >= series.questions.length) {
    return 'end-presentation';
  }

  const newSlideIndex = slideIndex + 1;
  const nextSlidePublicKey = series.questions[newSlideIndex]!.public_key;
  const nextSlide = getSlide(nextSlidePublicKey, series);
  return nextSlide?.linearNavigationSteps[0];
};

const calculateState = (
  {
    id,
    slidePublicKey,
    step,
    newStep,
    hasReachedEnd,
    slideStates,
    blankScreenVisible,
    qrCodePlacement,
    currentStep,
  }: Pick<
    InternalState,
    | 'id'
    | 'slidePublicKey'
    | 'step'
    | 'newStep'
    | 'hasReachedEnd'
    | 'slideStates'
    | 'blankScreenVisible'
    | 'qrCodePlacement'
    | 'currentStep'
  >,
  series: SeriesForPresentation,
): InternalState => {
  const currentSlide = getSlide(slidePublicKey, series);
  if (!currentSlide) {
    throw new Error(
      `Could not find slide with public key ${slidePublicKey} in series`,
    );
  }

  const isInQuizLobby =
    slideStates[slidePublicKey]?.started === false &&
    slideStates[slidePublicKey]?.starting === false;

  // all variables with the name "new" are temporarily used for linear navigation,
  // and will be cleaned up in LIVE-1214
  const linearNavigationSteps = currentSlide.linearNavigationSteps ?? [];
  const slideIndex = series.questions.indexOf(currentSlide);
  const hasNextStep = !hasReachedEnd && step < currentSlide.steps;
  const hasNewNextStep =
    !hasReachedEnd &&
    (newStep < linearNavigationSteps.length - 1 || isInQuizLobby);
  const hasNextSlide = !hasReachedEnd;
  const hasPreviousStep = !hasReachedEnd && step > 0;
  const hasNewPreviousStep = !hasReachedEnd && newStep > 0;
  const hasPreviousSlide = hasReachedEnd || slideIndex > 0;

  // if current step type is undefined it's likely an error
  // or un-migrated presentation
  const currentStepOrLinearNavigationStep =
    currentStep ?? linearNavigationSteps[newStep];

  const nextSlideInitialStep = getNextSlideInitialStep({
    slideIndex,
    series,
  });

  const nextStep = getNextStep({
    hasNewNextStep,
    linearNavigationSteps,
    newStep,
    hasNextSlide,
    isInQuizLobby,
    nextSlideInitialStep,
  });

  return {
    // From input
    id,
    slidePublicKey,
    step,
    newStep,
    hasReachedEnd,
    slideStates,
    blankScreenVisible,
    qrCodePlacement,
    isInQuizLobby,

    // Calculated values
    currentStep: currentStepOrLinearNavigationStep,
    nextStep,
    slideIndex,
    totalSlides: series.questions.length,
    hasNext: hasNextStep || hasNextSlide,
    hasNewNext: hasNewNextStep || hasNextSlide,
    hasNextSlide,
    hasNextStep,
    hasNewNextStep,
    hasPrevious: hasPreviousStep || hasPreviousSlide,
    hasNewPrevious: hasNewPreviousStep || hasPreviousSlide,
    hasPreviousSlide,
    hasPreviousStep,
    hasNewPreviousStep,
  };
};

export const createInitialState = (
  series: SeriesForPresentation,
  slidePublicKey?: string,
): InternalState => {
  if (series.questions.length === 0) {
    throw new Error('Series has no questions');
  }

  const currentSlidePublicKey =
    slidePublicKey || series.questions[0]!.public_key;

  const currentSlide = getSlide(currentSlidePublicKey, series);
  if (!currentSlide) {
    throw new Error(
      `Could not find slide with public key ${currentSlidePublicKey} in series`,
    );
  }

  return calculateState(
    {
      id: uuidv4(),
      step: 0,
      newStep: 0,
      slidePublicKey: currentSlidePublicKey,
      hasReachedEnd: false,
      slideStates: {
        ...(currentSlide.linearNavigationSteps[0] === 'quiz' && {
          [currentSlidePublicKey]: INITIAL_QUIZ_SLIDE_STATE,
        }),
      },
      blankScreenVisible: false,
      qrCodePlacement: { vertical: 'bottom', horizontal: 'left' },
      currentStep: undefined,
    },
    series,
  );
};

export const getSeriesUpdateState = (
  state: InternalState,
  series: SeriesForPresentation,
): InternalState => {
  if (series.questions.length === 0) {
    throw new Error('series has no questions');
  }

  const oldSlideIndex = state.slideIndex;

  let newSlideIndex = series.questions.findIndex(
    (s) => s.public_key === state.slidePublicKey,
  );

  const newStep = newSlideIndex === -1 ? 0 : state.step;
  const newLinNavStep = newSlideIndex === -1 ? 0 : state.newStep;

  if (newSlideIndex === -1) {
    // Slide has been removed from presentation
    newSlideIndex = Math.min(
      series.questions.length - 1,
      Math.max(0, oldSlideIndex - 1),
    );
  }

  const slide = series.questions[newSlideIndex];
  if (!slide) {
    throw new Error(`No slide found at new index ${newSlideIndex}`);
  }

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      slidePublicKey: slide.public_key,
      step: state.hasReachedEnd
        ? 0
        : Math.max(0, Math.min(newStep, slide.steps)),
      newStep: state.hasReachedEnd
        ? 0
        : Math.max(
            0,
            Math.min(
              newLinNavStep,
              slide?.linearNavigationSteps
                ? slide.linearNavigationSteps.length - 1
                : 0,
            ),
          ),
    },
    series,
  );
};

export const getResetStepState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on resetStep');
  invariant(internalSeries, 'series missing on resetStep');

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      step: 0,
      newStep: 0,
    },
    internalSeries,
  );
};

export const getNextState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
) => {
  invariant(state, 'state missing on next');

  if (state.hasNextStep) {
    return getNextStepState(state, internalSeries);
  }
  if (state.hasNextSlide) {
    return getNextSlideState(state, internalSeries);
  }
  throw new Error('Tried to pace to next when there is none');
};

// will be cleaned up in LIVE-1214
export const getNewNextState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
) => {
  invariant(state, 'state missing on newNext');

  if (state.hasNewNextStep) {
    return getNewNextStepState(state, internalSeries);
  }
  if (state.hasNextSlide) {
    return getNextSlideState(state, internalSeries);
  }
  throw new Error('Tried to pace to new next when there is none');
};

export const getNextSlideState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on nextSlide');
  invariant(internalSeries, 'internalSeries missing on nextSlide');
  if (!state.hasNextSlide) {
    throw new Error('tried to pace to next slide when there is none');
  }

  if (state.slideIndex === internalSeries.questions.length - 1) {
    // Pacing next on last slide
    return calculateState(
      {
        ...state,
        currentStep: undefined,
        hasReachedEnd: true,
      },
      internalSeries,
    );
  }
  const newSlideIndex = state.slideIndex + 1;
  const slidePublicKey = internalSeries.questions[newSlideIndex]!.public_key;

  const newCurrentSlide = getSlide(slidePublicKey, internalSeries);

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      step: 0,
      newStep: 0,
      slidePublicKey,
      slideStates: {
        ...state.slideStates,
        ...(newCurrentSlide?.linearNavigationSteps[0] === 'quiz' && {
          [slidePublicKey]: INITIAL_QUIZ_SLIDE_STATE,
        }),
      },
    },
    internalSeries,
  );
};

export const getNextStepState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on nextStep');
  invariant(internalSeries, 'internalSeries missing on nextStep');

  if (!state.hasNextStep) {
    throw new Error('tried to pace to next step when there is none');
  }

  const currentSlide = getSlide(state.slidePublicKey, internalSeries);
  const slideSteps = currentSlide?.steps ?? 0;
  // currentSlide.linearNavigationSteps will contain the initial step, which should not be counted
  const linearNavigationSteps = currentSlide?.linearNavigationSteps
    ? currentSlide.linearNavigationSteps.length - 1
    : 0;

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      step: Math.max(0, Math.min(state.step + 1, slideSteps)),
      newStep: Math.max(0, Math.min(state.newStep + 1, linearNavigationSteps)),
    },
    internalSeries,
  );
};

// will be cleaned up in LIVE-1214
export const getNewNextStepState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on newNextStep');
  invariant(internalSeries, 'internalSeries missing on newNextStep');

  if (!state.hasNewNextStep) {
    throw new Error('tried to pace to new next step when there is none');
  }

  const currentSlide = getSlide(state.slidePublicKey, internalSeries);
  const slideSteps = currentSlide?.steps ?? 0;
  // currentSlide.linearNavigationSteps will contain the initial step, which should not be counted
  const linearNavigationSteps = currentSlide?.linearNavigationSteps
    ? currentSlide.linearNavigationSteps.length - 1
    : 0;

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      step: Math.max(0, Math.min(state.step + 1, slideSteps)),
      newStep: Math.max(0, Math.min(state.newStep + 1, linearNavigationSteps)),
      slideStates: {
        ...state.slideStates,
        ...(state.isInQuizLobby && {
          [state.slidePublicKey]: START_QUIZ_SLIDE_STATE,
        }),
      },
    },
    internalSeries,
  );
};

export const getPreviousState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
) => {
  invariant(state, 'state missing on previous');

  if (state.hasPreviousStep) {
    return getPreviousStepState(state, internalSeries);
  }
  if (state.hasPreviousSlide) {
    return getPreviousSlideState(state, internalSeries);
  }
  throw new Error('tried to pace to previous when there is none');
};

// will be cleaned up in LIVE-1214
export const getNewPreviousState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
) => {
  invariant(state, 'state missing on newPrevious');

  if (state.hasNewPreviousStep) {
    return getNewPreviousStepState(state, internalSeries);
  }
  if (state.hasPreviousSlide) {
    return getPreviousSlideState(state, internalSeries);
  }
  throw new Error('tried to pace to new previous when there is none');
};

export const getPreviousSlideState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on previousSlide');
  invariant(internalSeries, 'internalSeries missing on previousSlide');
  if (!state.hasPreviousSlide) {
    throw new Error('tried to pace to previous slide when there is none');
  }

  const newSlideIndex = state.hasReachedEnd
    ? state.slideIndex
    : state.slideIndex - 1;
  const newSlide = internalSeries.questions[newSlideIndex];

  if (!newSlide) {
    throw new Error('No slide found at new index');
  }

  const slidePublicKey = newSlide.public_key;

  const newCurrentSlide = getSlide(slidePublicKey, internalSeries);

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      hasReachedEnd: false,
      slidePublicKey,
      step: newSlide.steps,
      newStep: newSlide?.linearNavigationSteps
        ? newSlide.linearNavigationSteps.length - 1
        : 0,
      slideStates: {
        ...state.slideStates,
        ...(newCurrentSlide?.linearNavigationSteps[0] === 'quiz' && {
          [slidePublicKey]: INITIAL_QUIZ_SLIDE_STATE,
        }),
      },
    },
    internalSeries,
  );
};

export const getPreviousStepState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on previousStep');
  invariant(internalSeries, 'internalSeries missing on previousStep');

  if (!state.hasPreviousStep) {
    throw new Error('tried to pace to previous step when there is none');
  }

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      step: Math.max(state.step - 1, 0),
      newStep: Math.max(state.newStep - 1, 0),
    },
    internalSeries,
  );
};

// will be cleaned up in LIVE-1214
export const getNewPreviousStepState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on newPreviousStep');
  invariant(internalSeries, 'internalSeries missing on newPreviousStep');

  if (!state.hasNewPreviousStep) {
    throw new Error('tried to pace to new previous step when there is none');
  }

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      step: Math.max(state.step - 1, 0),
      newStep: Math.max(state.newStep - 1, 0),
    },
    internalSeries,
  );
};

export const getSetActiveSlideState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
  newSlidePublicKey: InternalState['slidePublicKey'],
): InternalState => {
  invariant(state, 'state missing on setActiveSlide');
  invariant(internalSeries, 'internalSeries missing on setActiveSlide');

  const newSlide = internalSeries.questions.some(
    (q) => q.public_key === newSlidePublicKey,
  );

  if (!newSlide) {
    throw new Error('Tried to activate a slide that does not exist');
  }

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      slidePublicKey: newSlidePublicKey,
      step: 0,
      newStep: 0,
      hasReachedEnd: false,
    },
    internalSeries,
  );
};

export const getSetSlideState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
  publicKey: string,
  slideState: SlideState,
): InternalState => {
  invariant(state, 'state missing on setSlideState');
  invariant(internalSeries, 'internalSeries missing on setSlideState');

  addBreadcrumb({
    category: 'usePresentation',
    message: 'setSlideState action, keys to update:',
    data: { publicKey, slideState },
  });

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      slideStates: {
        ...state.slideStates,
        [publicKey]: slideState,
      },
    },
    internalSeries,
  );
};

export const getResetAllSlideStates = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
): InternalState => {
  invariant(state, 'state missing on resetAllSlideStates');
  invariant(internalSeries, 'internalSeries missing on resetAllSlideStates');

  return calculateState(
    {
      ...state,
      currentStep: undefined,
      slideStates: {},
    },
    internalSeries,
  );
};

export const getSetBlankScreenVisibleState = (
  state: InternalState | undefined,
  blankScreenVisible: boolean,
): InternalState => {
  invariant(state, 'state missing on setBlankScreenVisibleState');
  return {
    ...state,
    blankScreenVisible,
  };
};

export const getSetQrCodePlacementState = (
  state: InternalState | undefined,
  qrCodePlacement: QRCodePlacement,
): InternalState => {
  invariant(state, 'state missing on getSetQrCodePlacementState');
  return {
    ...state,
    qrCodePlacement,
  };
};

export const getSetCurrentStepState = (
  state: InternalState | undefined,
  internalSeries: SeriesForPresentation | undefined,
  step: 'view-answers' | 'hide-answers',
): InternalState => {
  invariant(state, 'state missing on setCurrentStepState');
  invariant(internalSeries, 'internalSeries missing on setCurrentStepState');
  invariant(step, 'step missing on setCurrentStepState');

  return calculateState(
    {
      ...state,
      currentStep: step,
    },
    internalSeries,
  );
};
